import { ExtractorDocumentType, postExtractDocumentBatch } from '@nemuru-eng/js-api-client';
import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';

export type ExtractionResult = Awaited<ReturnType<typeof postExtractDocumentBatch>>['results'][number];

export interface FileEntry {
  file: File;
  status: 'pending' | 'succeeded' | 'failed';
  result?: ExtractionResult;
  viewExpanded: boolean;
  assignedDocumentType?: ExtractorDocumentType;
  retryError?: string;
  rotationDegrees: number;
  /** `rotationDegrees` when extraction last succeeded; used to sync status after rotate. */
  rotationDegreesAtExtract?: number;
  /** Clockwise 90° steps applied via pdf-lib, mod 4; only used for PDFs. */
  pdfRotationSteps?: number;
  /** `pdfRotationSteps` when extraction last succeeded; used to sync status after rotate. */
  pdfRotationStepsAtExtract?: number;
}

interface DragState {
  source: number | null;
  over: number | null;
}

interface DocumentExtractorContextType {
  entries: FileEntry[];
  setEntries: React.Dispatch<React.SetStateAction<FileEntry[]>>;
  fileErrors: string[];
  setFileErrors: React.Dispatch<React.SetStateAction<string[]>>;
  apiError: string | null;
  setApiError: React.Dispatch<React.SetStateAction<string | null>>;
  isExtracting: boolean;
  setIsExtracting: React.Dispatch<React.SetStateAction<boolean>>;
  dragState: DragState;
  setDragState: React.Dispatch<React.SetStateAction<DragState>>;
  duplicatedDocTypes: Set<ExtractorDocumentType>;
  clearState: () => void;
}

const DocumentExtractorContext = createContext<DocumentExtractorContextType | null>(null);

export const useDocumentExtractorContext = (): DocumentExtractorContextType => {
  const context = useContext(DocumentExtractorContext);
  if (!context) throw new Error('useDocumentExtractorContext must be used within DocumentExtractorProvider');
  return context;
};

interface DocumentExtractorProviderProps {
  children: ReactNode;
}

export const DocumentExtractorProvider = ({ children }: DocumentExtractorProviderProps) => {
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [dragState, setDragState] = useState<DragState>({ source: null, over: null });

  const duplicatedDocTypes = useMemo(() => {
    const seen = new Set<ExtractorDocumentType>();
    const dupes = new Set<ExtractorDocumentType>();
    for (const e of entries) {
      if (e.status === 'succeeded' && e.result?.documentType) {
        if (seen.has(e.result.documentType)) dupes.add(e.result.documentType);
        else seen.add(e.result.documentType);
      }
    }
    return dupes;
  }, [entries]);

  const clearState = () => {
    setEntries([]);
    setFileErrors([]);
    setApiError(null);
    setIsExtracting(false);
    setDragState({ source: null, over: null });
  };

  return (
    <DocumentExtractorContext.Provider
      value={{
        entries,
        setEntries,
        fileErrors,
        setFileErrors,
        apiError,
        setApiError,
        isExtracting,
        setIsExtracting,
        dragState,
        setDragState,
        duplicatedDocTypes,
        clearState,
      }}
    >
      {children}
    </DocumentExtractorContext.Provider>
  );
};
